import { Clock, PauseCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';
import type { TicketDetail, TicketListItem } from '@/lib/types';
import { cn, formatDateTime } from '@/lib/utils';

type SlaTicket = Pick<
  TicketListItem,
  'responseDueAt' | 'resolutionDueAt' | 'responseBreachedAt' | 'resolutionBreachedAt' | 'slaPausedAt' | 'firstResponseAt' | 'resolvedAt'
>;

export function remaining(due: string, now = Date.now()) {
  const min = Math.round((new Date(due).getTime() - now) / 60_000);
  const abs = Math.abs(min);
  const text = abs < 60 ? `${abs} Min.` : abs < 48 * 60 ? `${Math.floor(abs / 60)} Std. ${abs % 60} Min.` : `${Math.floor(abs / 1440)} T. ${Math.floor((abs % 1440) / 60)} Std.`;
  return { min, text };
}

type Tone = 'ok' | 'warn' | 'breach' | 'done' | 'paused';

function state(due: string | null, doneAt: string | null, breachedAt: string | null, paused: boolean): { tone: Tone; label: string } | null {
  if (!due) return null;
  if (doneAt) return breachedAt ? { tone: 'breach', label: `verspätet erfüllt (${formatDateTime(doneAt)})` } : { tone: 'done', label: `erfüllt (${formatDateTime(doneAt)})` };
  if (paused) return { tone: 'paused', label: 'angehalten – wartet auf Kunde' };
  const r = remaining(due);
  if (r.min < 0) return { tone: 'breach', label: `überschritten seit ${r.text}` };
  return { tone: r.min <= 60 ? 'warn' : 'ok', label: `noch ${r.text} (bis ${formatDateTime(due)})` };
}

const TONE: Record<Tone, string> = {
  ok: 'bg-success/15 text-success',
  warn: 'bg-warning/20 text-amber-700 dark:text-amber-300',
  breach: 'bg-destructive/12 text-destructive',
  done: 'bg-secondary text-secondary-foreground',
  paused: 'bg-secondary text-muted-foreground',
};

/** Kompakte SLA-Anzeige für Ticketlisten: die nächste offene Frist. */
export function SlaBadge({ t }: { t: SlaTicket }) {
  const response = !t.firstResponseAt ? state(t.responseDueAt, null, t.responseBreachedAt, false) : null;
  const resolution = state(t.resolutionDueAt, t.resolvedAt, t.resolutionBreachedAt, Boolean(t.slaPausedAt));
  const s = response ?? resolution;
  if (!s || s.tone === 'done') return null;
  const due = response ? t.responseDueAt! : t.resolutionDueAt!;
  const short = s.tone === 'paused' ? 'pausiert' : s.tone === 'breach' ? 'SLA verletzt' : remaining(due).text;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap', TONE[s.tone])} title={`${response ? 'Reaktion' : 'Lösung'}: ${s.label}`}>
      {s.tone === 'paused' ? <PauseCircle className="size-3" /> : <Clock className="size-3" />}
      {short}
    </span>
  );
}

export function SlaCard({ t }: { t: TicketDetail }) {
  if (!t.slaPolicy && !t.contract) return null;
  const rows = [
    { label: 'Reaktion', s: state(t.responseDueAt, t.firstResponseAt, t.responseBreachedAt, false) },
    { label: 'Lösung', s: state(t.resolutionDueAt, t.resolvedAt, t.resolutionBreachedAt, Boolean(t.slaPausedAt)) },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Vertrag & SLA</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2 text-sm">
        {t.contract ? (
          <p>
            <Link to={`/contracts?id=${t.contract.id}`} className="text-primary hover:underline">
              {t.contract.number} · {t.contract.title}
            </Link>
          </p>
        ) : (
          <p className="text-muted-foreground">Kein Wartungsvertrag</p>
        )}
        {t.slaPolicy && <p className="text-muted-foreground">Richtlinie: {t.slaPolicy.name}</p>}
        {rows.map(
          (r) =>
            r.s && (
              <div key={r.label} className="flex items-start justify-between gap-2">
                <span className="text-muted-foreground">{r.label}</span>
                <span className={cn('rounded-md px-1.5 py-0.5 text-right text-xs font-medium', TONE[r.s.tone])}>{r.s.label}</span>
              </div>
            ),
        )}
      </CardContent>
    </Card>
  );
}
